/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { Platform, ScrollView, StyleSheet, View } from 'react-native';
import {
  KeyboardAwareScrollView,
  type KeyboardAwareScrollViewProps,
  type KeyboardAwareScrollViewRef,
} from 'react-native-keyboard-controller';

export type ModalAwareScrollViewProps = KeyboardAwareScrollViewProps;

/**
 * Drop-in de `KeyboardAwareScrollView` para modales y sheets:
 *
 * - **Nativo**: `KeyboardAwareScrollView` (acomoda el contenido al teclado).
 * - **Web**: `View` wrapper + `ScrollView` plano. En web no hay teclado virtual
 *   que acomodar — los bindings de KASV son no-op — pero su wrapper
 *   (`ClippingScrollView` → `View` con `flexGrow:1/flexShrink:1` y
 *   `min-height:auto` implícito) no puede encoger por debajo del contenido en
 *   CSS. Dentro de una card con `maxHeight` el scroll nunca se activaba y el
 *   modal quedaba cortado con el footer empujado fuera del viewport
 *   (issue #1469). El wrapper acá replica el layout de KASV pero con
 *   `minHeight:0`, así el flex item sí cede y el ScrollView interno scrollea.
 *
 * Misma API: las props solo-nativas (`bottomOffset`, `mode`, `enabled`, etc.)
 * se ignoran en web.
 */
export const ModalAwareScrollView = React.forwardRef<KeyboardAwareScrollViewRef, ModalAwareScrollViewProps>(
  function ModalAwareScrollView(
    {
      bottomOffset,
      disableScrollOnKeyboardHide,
      enabled,
      extraKeyboardSpace,
      mode,
      ScrollViewComponent,
      ...scrollProps
    },
    ref,
  ) {
    if (Platform.OS === 'web') {
      return (
        <View style={styles.webClipper}>
          <ScrollView
            ref={ref as React.Ref<ScrollView>}
            {...scrollProps}
            style={[styles.webInner, scrollProps.style]}
          />
        </View>
      );
    }
    return (
      <KeyboardAwareScrollView
        ref={ref}
        bottomOffset={bottomOffset}
        disableScrollOnKeyboardHide={disableScrollOnKeyboardHide}
        enabled={enabled}
        extraKeyboardSpace={extraKeyboardSpace}
        mode={mode}
        ScrollViewComponent={ScrollViewComponent}
        {...scrollProps}
      />
    );
  },
);

const styles = StyleSheet.create({
  /**
   * Mismo contrato que el wrapper de KASV (`flexGrow:1, flexShrink:1`) pero con
   * `minHeight:0`: sin esto el `min-height:auto` de CSS impide que el item ceda
   * por debajo del contenido y el modal se desborda en web.
   */
  webClipper: {
    flexGrow: 1,
    flexShrink: 1,
    minHeight: 0,
    minWidth: 0,
  },
  /**
   * En web `flex-shrink` default de un flex item es 0 (RNW fija el default de
   * Yoga): sin `flexShrink:1` el scroll queda a altura de contenido aunque el
   * wrapper haya cedido, y la card lo recorta con overflow:hidden.
   */
  webInner: {
    flexShrink: 1,
    minHeight: 0,
  },
});
